import { X } from 'lucide-react';
import { useEffect } from 'react';

import { cn } from '@/lib/cn';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}

/** Simple accessible dialog. Escape closes, click-outside closes,
 * focus lock is intentionally light — full focus-trap can be added if
 * we ever ship a form with menus/comboboxes nested inside. */
export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  children,
}: Props): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const sizeCls = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl' }[size];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mb-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 bg-mb-text/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        className={cn(
          'relative w-full mb-card p-6 max-h-[90vh] overflow-y-auto',
          sizeCls,
        )}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <h2 id="mb-modal-title" className="font-display text-lg font-semibold text-mb-text">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="text-mb-text-muted hover:text-mb-text"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
